import { useRef, useState } from "react";
import { Upload, X, Plus } from "lucide-react";
import { toast } from "sonner";
import { http, apiError, fileUrl } from "@/lib/api";

const input = "mt-1 h-11 w-full rounded-lg border border-black/20 bg-white px-3 text-sm";

export function Uploader({ value, onChange, testid }) {
  const ref = useRef();
  const [busy, setBusy] = useState(false);
  const upload = async (f) => {
    if (!f) return;
    setBusy(true);
    const fd = new FormData(); fd.append("file", f);
    try { const { data } = await http.post("/admin/upload", fd); onChange(data.url); toast.success("Ficheiro carregado"); }
    catch (e) { toast.error(apiError(e)); } finally { setBusy(false); ref.current.value = ""; }
  };
  return (
    <div className="mt-1 flex items-center gap-3">
      {value && (/\.pdf$/i.test(value) ? <a href={fileUrl(value)} target="_blank" rel="noreferrer" className="text-sm underline">PDF</a> : <img src={fileUrl(value)} alt="" className="h-14 w-14 rounded-lg object-cover border" />)}
      <input value={value || ""} onChange={(e) => onChange(e.target.value)} placeholder="URL ou carregar ficheiro" className={input + " mt-0 flex-1"} data-testid={`${testid}-url`} />
      <input ref={ref} type="file" accept="image/*,application/pdf" hidden onChange={(e) => upload(e.target.files[0])} data-testid={`${testid}-file`} />
      <button type="button" onClick={() => ref.current.click()} disabled={busy} className="h-11 px-3 rounded-lg border bg-white inline-flex items-center gap-2 text-sm font-semibold" data-testid={`${testid}-upload`}><Upload className="h-4 w-4" />{busy ? "…" : "Carregar"}</button>
    </div>
  );
}

function Position({ form, set }) {
  const pick = (e) => {
    const r = e.currentTarget.getBoundingClientRect();
    set("x", Math.round(((e.clientX - r.left) / r.width) * 100));
    set("y", Math.round(((e.clientY - r.top) / r.height) * 100));
  };
  return (
    <div className="mt-1">
      <div onClick={pick} className="relative aspect-[16/10] rounded-xl bg-[#e9f5e1] border cursor-crosshair" data-testid="field-position-map">
        <span className="absolute h-5 w-5 -translate-x-1/2 -translate-y-1/2 rounded-full bg-[var(--clay)] ring-4 ring-white" style={{ left: `${form.x ?? 50}%`, top: `${form.y ?? 50}%` }} />
      </div>
      <p className="text-xs text-slate-600 mt-1">x: {form.x ?? 50}% · y: {form.y ?? 50}%</p>
    </div>
  );
}

function Items({ value = [], onChange }) {
  const upd = (i, k, v) => onChange(value.map((it, j) => (j === i ? { ...it, [k]: v } : it)));
  return (
    <div className="mt-1 space-y-3">
      {value.map((it, i) => (
        <div key={i} className="rounded-xl border bg-white p-3 space-y-2" data-testid={`gallery-item-row-${i}`}>
          <div className="flex gap-2">
            <select value={it.type || "image"} onChange={(e) => upd(i, "type", e.target.value)} className="h-11 rounded-lg border px-2 text-sm"><option value="image">Fotografia</option><option value="video">Vídeo</option></select>
            <button type="button" onClick={() => onChange(value.filter((_, j) => j !== i))} className="ml-auto h-11 w-11 grid place-items-center rounded-lg border" aria-label="Remover"><X className="h-4 w-4" /></button>
          </div>
          <Uploader value={it.url} onChange={(v) => upd(i, "url", v)} testid={`gallery-item-${i}`} />
          <input value={it.caption || ""} onChange={(e) => upd(i, "caption", e.target.value)} placeholder="Legenda" className={input} />
          <input value={it.alt || ""} onChange={(e) => upd(i, "alt", e.target.value)} placeholder="Texto alternativo (acessibilidade)" className={input} />
        </div>
      ))}
      <button type="button" onClick={() => onChange([...value, { type: "image", url: "", caption: "", alt: "" }])} className="h-11 px-4 rounded-lg border bg-white inline-flex items-center gap-2 text-sm font-semibold" data-testid="gallery-add-item"><Plus className="h-4 w-4" />Adicionar item</button>
    </div>
  );
}

export default function Field({ f, form, set, refs, isAdmin }) {
  const v = form[f.key];
  const id = `field-${f.key}`;
  const label = <span className="text-sm font-semibold">{f.label}{f.required && " *"}</span>;
  if (f.type === "bool") {
    const locked = f.key === "published" && !isAdmin;
    return (
      <label className="flex items-center gap-3 min-h-[44px]">
        <input type="checkbox" checked={!!v} disabled={locked} onChange={(e) => set(f.key, e.target.checked)} className="h-5 w-5 accent-[var(--leaf)]" data-testid={id} />
        <span className="text-sm font-semibold">{f.label}{locked && " (só administradores)"}</span>
      </label>
    );
  }
  let control;
  if (f.type === "textarea") control = <textarea rows={f.rows || 3} value={v || ""} onChange={(e) => set(f.key, e.target.value)} className="mt-1 w-full rounded-lg border border-black/20 bg-white p-3 text-sm" data-testid={id} />;
  else if (f.type === "select") control = <select value={v || ""} onChange={(e) => set(f.key, e.target.value)} className={input} data-testid={id}>{f.options.map(([k, l]) => <option key={k} value={k}>{l}</option>)}</select>;
  else if (f.type === "ref") control = <select value={v || ""} onChange={(e) => set(f.key, e.target.value)} className={input} data-testid={id}><option value="">—</option>{(refs[f.ref] || []).map((r) => <option key={r.id} value={r.id}>{r.name || r.title}</option>)}</select>;
  else if (f.type === "refs" || f.type === "multi") {
    const opts = f.type === "refs" ? (refs[f.ref] || []).map((r) => [r.id, r.name || r.title]) : f.options.map((o) => [o, o]);
    const cur = v || [];
    control = (
      <div className="mt-1 flex flex-wrap gap-2" data-testid={id}>
        {opts.map(([k, l]) => { const on = cur.includes(k); return <button type="button" key={k} aria-pressed={on} onClick={() => set(f.key, on ? cur.filter((x) => x !== k) : [...cur, k])} className={`px-3 py-2 rounded-full text-xs font-semibold border ${on ? "bg-[var(--leaf)] text-white border-[var(--leaf)]" : "bg-white"}`}>{l}</button>; })}
        {!opts.length && <span className="text-xs text-slate-500">Sem opções nesta edição.</span>}
      </div>
    );
  } else if (f.type === "image") control = <Uploader value={v} onChange={(x) => set(f.key, x)} testid={id} />;
  else if (f.type === "position") control = <Position form={form} set={set} />;
  else if (f.type === "items") control = <Items value={v} onChange={(x) => set(f.key, x)} />;
  else {
    const type = { number: "number", date: "date", datetime: "datetime-local", color: "color" }[f.type] || "text";
    control = <input type={type} required={f.required} value={v ?? ""} onChange={(e) => set(f.key, f.type === "number" ? (e.target.value === "" ? "" : Number(e.target.value)) : e.target.value)} placeholder={f.hint || ""} className={input} data-testid={id} />;
  }
  return <label className="block" htmlFor={id}>{label}{control}</label>;
}
